import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { ArrowRight } from "lucide-react"

import { BotonComentario } from "@/components/Comentarios"
import { LeyendaDelCorte } from "@/components/CorteReal"
import { refCuenta } from "@/routes/comentarios/api"
import { DataState } from "@/components/DataState"
import { MetricCard, MetricRow } from "@/components/MetricCard"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth, useRbac } from "@/lib/auth"
import { formatAmount } from "@/lib/format"
import { agrupar, bucketsDe, ultimoBucketReal } from "@/lib/periodo"
import { cn } from "@/lib/utils"

import { fetchCajaBancos, fetchCashflow } from "@/routes/finance/api"
import { useFinanceFilters } from "@/routes/finance/context"
import { PisarCeldaDialog, type CeldaDeCaja } from "@/routes/finance/PisarCeldaDialog"
import { nombreCuenta, serieMensual } from "@/routes/finance/mapping"
import { BotonDescargar } from "@/components/BotonDescargar"
import { t } from "@/lib/i18n"

/**
 * Flujo de caja: lo unico que no admite optimismo.
 *
 * El P&L dice si el negocio gana; la caja dice si llega a fin de mes, y son
 * preguntas distintas. Un mes puede cerrar con EBITDA positivo y caja negativa
 * —cobranza que se corre, detraccion que se deposita, sueldos que se pagan
 * antes de cobrar— y esa diferencia es exactamente lo que esta pantalla existe
 * para mostrar.
 *
 * DOS REGLAS QUE NO SE PUEDEN MEZCLAR:
 *
 *  1. EL SIGNO SE CONSERVA, al reves que en el P&L. Una entrada es positiva y
 *     una salida negativa: en un reporte de caja el signo ES el dato.
 *
 *  2. EL ACUMULADO ES UN SALDO, NO UN IMPORTE. Al agrupar por trimestre, la
 *     caja acumulada del trimestre es la del ULTIMO mes, no la suma de los
 *     tres. Sumarla da una cifra varias veces mayor y perfectamente creible.
 *     Por eso `agrupar` recibe "ultimo" en esa fila y solo en esa.
 */
const MES_CORTO = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"]

export function CashFlowPage() {
  const { activeTenantId } = useAuth()
  const { can } = useRbac()
  const { anio, pais, paisQuery, listo, granularidad } = useFinanceFilters()
  /*
    EL FORECAST SE PISA DESDE AQUÍ. Cesar, 2026-10-04: «debería de poder editar
    el forecast». Las celdas a la derecha del corte bancario salen de la
    proyección por categoría y se pisan con un clic (sólo por mes: un
    trimestre pisado no significa nada). Ver `PisarCeldaDialog`.
  */
  const [celda, setCelda] = React.useState<CeldaDeCaja | null>(null)
  const puedePisar = can("edit") && granularidad === "mes"

  const caja = useQuery({
    queryKey: ["finance-cashflow", activeTenantId, anio, pais],
    queryFn: () => fetchCashflow(anio, paisQuery),
    enabled: Boolean(activeTenantId),
  })
  /*
    LA CAJA DESDE LOS BANCOS. Cesar, 2026-10-04: «deberíamos de tener la
    sumatoria de los movimientos de bancos declarados por mes cerrados y luego
    meses a futuros con forecast». Hasta el último mes con movimientos cargados
    manda el extracto; desde el siguiente, el forecast (escenario moderado). La
    tabla de la hoja se queda debajo, como referencia de lo que el modelo decía.
  */
  const bancos = useQuery({
    queryKey: ["finance-caja-bancos", activeTenantId, anio, pais],
    queryFn: () => fetchCajaBancos(anio, paisQuery),
    enabled: Boolean(activeTenantId),
  })

  const buckets = bucketsDe(granularidad, anio)
  // La linea roja. Misma regla que el P&L; ver `ultimoBucketReal`.
  const corte = caja.data?.corte
  const bucketReal = ultimoBucketReal(buckets, corte?.mes)
  const entradas = agrupar(serieMensual(caja.data?.entradas), granularidad, anio)
  const salidas = agrupar(serieMensual(caja.data?.salidas), granularidad, anio)
  const neto = agrupar(serieMensual(caja.data?.neto), granularidad, anio)
  const acumulado = agrupar(serieMensual(caja.data?.acumulado), granularidad, anio, "ultimo")

  const totalEntradas = entradas.reduce((s, b) => s + b.valor, 0)
  const totalSalidas = salidas.reduce((s, b) => s + b.valor, 0)
  const cierre = acumulado[acumulado.length - 1]?.valor ?? 0

  const enRojo = neto.filter((b) => b.valor < 0)
  const peor = neto.reduce((p, b) => (b.valor < p.valor ? b : p), neto[0])

  const b = bancos.data
  const ultimoBancario = b?.ultimo_mes_bancario ?? null
  const bucketBancario = ultimoBancario ? ultimoBucketReal(buckets, ultimoBancario) : null
  const netoB = agrupar(serieMensual(b?.neto), granularidad, anio)
  const acumB = agrupar(serieMensual(b?.acumulado), granularidad, anio, "ultimo")
  const entradasReales = ultimoBancario
    ? Object.entries(b?.entradas ?? {}).filter(([m]) => Number(m) <= ultimoBancario).reduce((t, [, v]) => t + v, 0)
    : 0
  const salidasReales = ultimoBancario
    ? Object.entries(b?.salidas ?? {}).filter(([m]) => Number(m) <= ultimoBancario).reduce((t, [, v]) => t + v, 0)
    : 0

  return (
    <div className="space-y-4">
      <MetricRow className="lg:grid-cols-4">
        <MetricCard label={t("Caja en bancos hoy")} value={b?.caja_bancos_hoy ?? 0} loading={bancos.isLoading} tono="destacado" />
        <MetricCard label={ultimoBancario ? `${t("Entradas reales hasta")} ${MES_CORTO[ultimoBancario - 1]}` : t("Entradas reales")}
                    value={entradasReales} loading={bancos.isLoading} />
        <MetricCard label={ultimoBancario ? `${t("Salidas reales hasta")} ${MES_CORTO[ultimoBancario - 1]}` : t("Salidas reales")}
                    value={salidasReales} loading={bancos.isLoading} />
        <MetricCard label={`${t("Acumulado a dic")} ${anio} · ${t("bancos + forecast")}`}
                    value={acumB[acumB.length - 1]?.valor ?? 0} loading={bancos.isLoading} />
      </MetricRow>

      <WorkPanel
        title={`${t("Caja")} ${anio} · ${t("bancos hasta")} ${ultimoBancario ? MES_CORTO[ultimoBancario - 1] : "—"}, ${t("forecast después")}`}
        meta={
          <span className="text-2xs text-muted-foreground">
            {b ? `${b.movimientos} ${t("movimiento(s) cargados")}${b.no_considerados ? ` · ${b.no_considerados} ${t("apartado(s)")}` : ""}${b.sin_cotizacion ? ` · ${b.sin_cotizacion} ${t("sin cotización (no se cuentan)")}` : ""}` : ""}
          </span>
        }
        actions={
          <Link to="/contabilidad/maestros/movimientos-bancarios"
                className="inline-flex items-center gap-1 text-2xs text-muted-foreground hover:text-foreground hover:underline">
            {t("Cómo se arma: los movimientos")} <ArrowRight className="h-3 w-3" />
          </Link>
        }
        footnote={b?.nota}
      >
        <DataState
          loading={bancos.isLoading}
          error={bancos.error}
          empty={Boolean(b) && !b?.movimientos}
          emptyMessage={t("Todavía no hay movimientos bancarios cargados para este año.")}
          emptyHint={t("Carga el extracto de cada cuenta en Contabilidad › Maestros › Carga por archivo › «Movimientos bancarios». Hasta entonces la caja sale de tu hoja (abajo) y del forecast.")}
          onRetry={() => void bancos.refetch()}
          rows={5}
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[16rem]">{t("Concepto")}</TableHead>
                  {buckets.map((bk, i) => (
                    <TableHead key={bk.label} title={bk.nombre}
                               className={cn("w-24 text-right", i === bucketBancario && "corte-real")}>
                      {bk.label}
                    </TableHead>
                  ))}
                  <TableHead className="w-28 text-right">{t("Año")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(b?.categorias ?? []).map((c) => {
                  const serie = agrupar(serieMensual(c.meses), granularidad, anio)
                  return (
                    <TableRow key={c.categoria}>
                      <TableCell>
                        <span className="flex items-center gap-1.5">
                          {t(c.nombre)}
                          <BotonComentario ancla={{ ambito: "cifra", referencia: refCuenta(`caja:${c.categoria}`, anio),
                                                    rotulo: `${t("Caja")} · ${t(c.nombre)} · ${anio}`, anio }} />
                        </span>
                      </TableCell>
                      {serie.map((v, i) => {
                        const mesN = i + 1
                        const futuro = granularidad === "mes" && mesN > (ultimoBancario ?? 0)
                        const pisada = c.pisados.includes(mesN)
                        const detalle = b?.pisadas.find((p) => p.mes === mesN && p.categoria === c.categoria) ?? null
                        const abrir = () => setCelda({
                          pais: paisQuery || undefined, anio, mes: mesN, categoria: c.categoria, nombre: c.nombre,
                          valor: v.valor, pisada: detalle,
                        })
                        return (
                          <TableCell key={i}
                                     title={pisada ? `${t("Pisada")}: ${detalle?.motivo ?? ""}` : futuro && puedePisar ? t("Pulsa para pisar esta celda") : undefined}
                                     onClick={futuro && puedePisar ? abrir : undefined}
                                     className={cn("num", v.valor < 0 && "text-destructive", i === bucketBancario && "corte-real",
                                                   futuro && "text-muted-foreground",
                                                   futuro && puedePisar && "cursor-pointer hover:bg-accent",
                                                   // UNA PISADA NO SE VE «EN EDICIÓN»: un punto de margen, y el
                                                   // motivo al pasar el cursor. Cesar ya pidió eso en el forecast.
                                                   pisada && "font-medium text-foreground punto-margen")}>
                            {formatAmount(v.valor)}
                          </TableCell>
                        )
                      })}
                      <TableCell className="num font-medium">{formatAmount(c.total)}</TableCell>
                    </TableRow>
                  )
                })}
                <TableRow className="font-medium">
                  <TableCell>{t("Neto del mes")} <span className="text-2xs font-normal text-muted-foreground">· {t("bancos, y forecast después del corte")}</span></TableCell>
                  {netoB.map((v, i) => (
                    <TableCell key={i} className={cn("num", v.valor < 0 && "text-destructive", i === bucketBancario && "corte-real")}>
                      {formatAmount(v.valor)}
                    </TableCell>
                  ))}
                  <TableCell className="num">{formatAmount(netoB.reduce((t2, v) => t2 + v.valor, 0))}</TableCell>
                </TableRow>
                <TableRow className="bg-surface-sunken font-medium">
                  <TableCell>{t("Caja acumulada")}</TableCell>
                  {acumB.map((v, i) => (
                    <TableCell key={i} className={cn("num", v.valor < 0 && "text-destructive", i === bucketBancario && "corte-real")}>
                      {formatAmount(v.valor)}
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <p className="px-2.5 pb-2 pt-1 text-2xs text-muted-foreground">
            {b?.saldo_inicial_declarado
              ? `${t("Arranca en el saldo inicial declarado de las cuentas")}: ${formatAmount(b.saldo_inicial)}.`
              : t("Ninguna cuenta declara saldo inicial: el acumulado arranca en cero. Decláralo en Bancos y tipos de cambio.")}
            {" "}{t("La línea roja es el último mes con extracto cargado; lo que sigue es forecast.")}
            {puedePisar ? ` ${t("Las celdas de forecast se pisan con un clic.")}` : ""}
          </p>
          {(b?.pisadas.length ?? 0) > 0 ? (
            <ul className="space-y-0.5 px-2.5 pb-2 text-2xs text-muted-foreground">
              {b?.pisadas.map((p) => (
                <li key={`${p.mes}-${p.categoria}`}>
                  <span className="text-foreground">{t(b.categorias.find((c) => c.categoria === p.categoria)?.nombre ?? p.categoria)} · {MES_CORTO[p.mes - 1]}</span>
                  {": "}{formatAmount(p.valor_usd)}
                  {p.revierte_a != null ? ` (${t("la proyección daba")} ${formatAmount(p.revierte_a)})` : ""}
                  {p.motivo ? ` — ${p.motivo}` : ""}{p.por ? ` · ${p.por}` : ""}
                </li>
              ))}
            </ul>
          ) : null}
        </DataState>
      </WorkPanel>

      <PisarCeldaDialog celda={celda} open={Boolean(celda)} onOpenChange={(v) => { if (!v) setCelda(null) }} />

      {/*
        LA CAJA SEGÚN LA HOJA, debajo y como referencia. Era la única fuente
        hasta el 2026-10-04 y sigue siendo lo que el modelo de gestión decía;
        comparar las dos es lo que enseña cuánto de lo modelado pasó de verdad.
      */}
      {/*
        CUATRO tarjetas y no tres, porque las dos ultimas son cosas distintas
        que se confundian en una sola.

        La tercera decia «Caja al cierre del ano» y no era caja: era lo que el
        modelo acumulo, y ademas arrancando de cero cada enero. Medido sobre los
        datos reales, el modelo acumula -39.484 al cierre de 2026 y los bancos
        tienen 112.917 — el bloque `cashflow` recoge los flujos que alguien
        modelo, no todos los movimientos de las cuentas.

        Poner las dos al lado convierte esa brecha en un dato en vez de en un
        numero que se lee como lo que no es.
      */}
      <MetricRow className="lg:grid-cols-4">
        <MetricCard label={`Entradas ${anio}`} value={totalEntradas} loading={caja.isLoading} />
        <MetricCard label={`Salidas ${anio}`} value={totalSalidas} loading={caja.isLoading} />
        <MetricCard
          label={`Acumulado del modelo a dic ${anio}`}
          value={cierre}
          loading={caja.isLoading}
          tono="destacado"
        />
        <MetricCard
          label={t("Caja real en bancos")}
          value={caja.data?.caja_bancos ?? 0}
          loading={caja.isLoading}
        />
      </MetricRow>

      {caja.data ? (
        <p className="max-w-[78ch] text-2xs leading-5 text-muted-foreground">
          {t("El acumulado arranca en")}{" "}
          <span className="num-strong">{formatAmount(caja.data.saldo_inicial)}</span>{t(", que es donde cerró el año anterior")}{caja.data.acumula_desde ? ` (se acumula desde ${caja.data.acumula_desde})` : null}.{" "}
          {!caja.data.saldo_inicial_completo
            ? "Este es el primer año con datos, así que no arrastra nada: si la sociedad operaba antes, falta su saldo de apertura. "
            : ""}
          {t("No es lo mismo que la caja real: el modelo recoge los flujos que alguien modeló, no todos los movimientos de las cuentas bancarias.")}</p>
      ) : null}

      <WorkPanel
        actions={<BotonDescargar recurso="flujo-caja" filtros={{ anio, pais: paisQuery }} />}
        title={`${t("Caja según tu hoja")} ${anio} · ${t("referencia")}`}
        meta={
          <span className="flex items-center gap-1.5 text-2xs text-muted-foreground">
            <Badge variant="outline">{caja.data?.moneda ?? "USD"}</Badge>
            {enRojo.length > 0 ? (
              <Badge variant="warning-soft">
                {enRojo.length} {granularidad === "mes" ? "meses" : "períodos"}{" "}{t("en negativo")}{peor ? ` · el más ajustado, ${peor.label}` : ""}
              </Badge>
            ) : (
              <span>{t("Ningún período cierra con neto negativo")}</span>
            )}
          </span>
        }
        footnote={t("Un neto negativo no es un error: es un período que se financia con la caja del anterior. Lo que hay que mirar es la fila acumulada. Y ojo: la caja acumulada arranca en cero cada año — es el movimiento del ejercicio, no el saldo bancario.")}
      >
        <DataState
          loading={caja.isLoading}
          error={caja.error}
          empty={Boolean(caja.data) && (caja.data?.cuentas ?? []).length === 0}
          emptyMessage={`No hay flujo de caja cargado para ${anio}.`}
          emptyHint={t("Tu hoja de gestión aún no está cargada para este año. Se carga en Primeros pasos, en «Tu facturación».")}
          onRetry={() => void caja.refetch()}
          rows={6}
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[16rem]">{t("Concepto")}</TableHead>
                  {buckets.map((b, i) => (
                    <TableHead
                      key={b.label}
                      title={b.nombre}
                      className={cn("w-24 text-right", i === bucketReal && "corte-real")}
                    >
                      {b.label}
                    </TableHead>
                  ))}
                  <TableHead className="w-28 text-right">{t("Total")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(caja.data?.cuentas ?? []).map((cuenta) => (
                  <TableRow key={cuenta.account}>
                    <TableCell>{nombreCuenta(cuenta.account)}</TableCell>
                    {agrupar(serieMensual(cuenta.meses), granularidad, anio).map((b, i) => (
                      <TableCell
                        key={b.label}
                        className={cn("num", b.valor < 0 && "text-muted-foreground",
                                      i === bucketReal && "corte-real")}
                      >
                        {b.valor === 0 ? "" : formatAmount(b.valor)}
                      </TableCell>
                    ))}
                    <TableCell className="num font-medium">{formatAmount(cuenta.total)}</TableCell>
                  </TableRow>
                ))}

                <TableRow className="bg-surface-sunken font-semibold">
                  <TableCell>{t("Neto del período")}</TableCell>
                  {neto.map((b, i) => (
                    <TableCell
                      key={b.label}
                      className={cn("num", b.valor < 0 && "text-destructive",
                                    i === bucketReal && "corte-real")}
                    >
                      {formatAmount(b.valor)}
                    </TableCell>
                  ))}
                  <TableCell className="num">
                    {formatAmount(neto.reduce((s, b) => s + b.valor, 0))}
                  </TableCell>
                </TableRow>
                <TableRow className="bg-brand-soft font-semibold">
                  <TableCell>{t("Caja acumulada")}</TableCell>
                  {acumulado.map((b, i) => (
                    <TableCell
                      key={b.label}
                      className={cn("num", b.valor < 0 && "text-destructive",
                                    i === bucketReal && "corte-real")}
                    >
                      {formatAmount(b.valor)}
                    </TableCell>
                  ))}
                  <TableCell className={cn("num", cierre < 0 && "text-destructive")}>
                    {formatAmount(cierre)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <LeyendaDelCorte corte={corte} bucketReal={bucketReal} className="pt-2" />
        </DataState>
      </WorkPanel>
    </div>
  )
}
